// Square API integration for Chef KnifeWorks - Production
const SQUARE_BASE_URL = 'https://connect.squareup.com/v2';
const ACCESS_TOKEN = 'EAAAl6mMU-kFb0tSokHVDAtC0ZxcOjSkxYZ--xwbeEEL6VNn7wVWFLfkvairw-cT';
const APP_ID = 'sq0idp-h2kYxRfFoRgNy6VY5hG9Ng';

export async function findAppointment(lookup) {
  try {
    // Search for customer by email or phone
    const customerResponse = await fetch(`${SQUARE_BASE_URL}/customers/search`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        filter: {
          email_address: {
            exact: lookup.includes('@') ? lookup : undefined
          },
          phone_number: {
            exact: !lookup.includes('@') ? lookup : undefined
          }
        }
      })
    });

    if (!customerResponse.ok) {
      const errorData = await customerResponse.json();
      console.error('Customer search error:', errorData);
      throw new Error('Failed to search customers');
    }

    const customerData = await customerResponse.json();
    
    if (!customerData.customers || customerData.customers.length === 0) {
      return {
        success: false,
        error: 'No customer found with that information'
      };
    }

    const customer = customerData.customers[0];

    // Try multiple approaches to find appointments
    
    // Approach 1: Search bookings by customer
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

    const appointmentResponse = await fetch(`${SQUARE_BASE_URL}/bookings/search`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        filter: {
          customer_id_filter: {
            all: [customer.id]
          },
          start_at_range: {
            start_at: startDate.toISOString(),
            end_at: endDate.toISOString()
          }
        },
        limit: 25
      })
    });

    if (!appointmentResponse.ok) {
      const errorData = await appointmentResponse.json();
      console.error('Appointment search error:', errorData);
      
      // Try alternative approach - list all bookings and filter
      const listResponse = await fetch(`${SQUARE_BASE_URL}/bookings?limit=100`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      });

      if (!listResponse.ok) {
        throw new Error('Failed to retrieve bookings');
      }

      const listData = await listResponse.json();
      const allBookings = listData.bookings || [];
      
      // Filter bookings for today and this customer
      const todayBookings = allBookings.filter(booking => 
        booking.customer_id === customer.id &&
        new Date(booking.start_at) >= startDate &&
        new Date(booking.start_at) < endDate
      );

      if (todayBookings.length === 0) {
        return {
          success: false,
          error: 'No appointment found for today'
        };
      }

      return {
        success: true,
        customer,
        appointment: todayBookings[0]
      };
    }

    const appointmentData = await appointmentResponse.json();
    
    const appointment = appointmentData.bookings?.find(booking => 
      booking.customer_id === customer.id &&
      new Date(booking.start_at) >= startDate &&
      new Date(booking.start_at) < endDate
    );

    if (!appointment) {
      return {
        success: false,
        error: 'No appointment found for today'
      };
    }

    return {
      success: true,
      customer,
      appointment
    };

  } catch (error) {
    console.error('Error finding appointment:', error);
    return {
      success: false,
      error: 'Unable to search appointments at this time'
    };
  }
}

export async function markCheckedIn(appointmentId) {
  try {
    // First, get the current appointment to preserve existing notes
    const getResponse = await fetch(`${SQUARE_BASE_URL}/bookings/${appointmentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    if (!getResponse.ok) {
      throw new Error('Failed to retrieve appointment');
    }

    const appointmentData = await getResponse.json();
    const existingNote = appointmentData.booking?.customer_note || '';
    const checkInNote = `Checked in via QR at ${new Date().toLocaleString()}`;
    const newNote = existingNote ? `${existingNote}\n${checkInNote}` : checkInNote;

    // Update appointment with check-in note
    const response = await fetch(`${SQUARE_BASE_URL}/bookings/${appointmentId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        booking: {
          id: appointmentId,
          version: appointmentData.booking.version,
          customer_note: newNote
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Check-in error:', errorData);
      throw new Error('Failed to update appointment');
    }

    return {
      success: true
    };

  } catch (error) {
    console.error('Error marking checked in:', error);
    return {
      success: false,
      error: 'Unable to check in at this time'
    };
  }
}

export async function submitCheckIn(appointmentId, photoFile) {
  try {
    let photoUrl = null;

    // Upload photo if provided (mock implementation for now)
    if (photoFile) {
      // In a real implementation, you would upload to a storage service
      // For now, we'll create a mock URL
      photoUrl = `https://storage.example.com/photos/${Date.now()}-${photoFile.name}`;
      console.log('Photo uploaded to:', photoUrl);
    }

    // Get current appointment to preserve existing notes
    const getResponse = await fetch(`${SQUARE_BASE_URL}/bookings/${appointmentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    if (!getResponse.ok) {
      throw new Error('Failed to retrieve appointment');
    }

    const appointmentData = await getResponse.json();
    const existingNote = appointmentData.booking?.customer_note || '';
    const checkInNote = `Checked in via QR at ${new Date().toLocaleString()}${photoUrl ? `. Photo: ${photoUrl}` : ''}`;
    const newNote = existingNote ? `${existingNote}\n${checkInNote}` : checkInNote;

    // Update appointment with final check-in info
    const updateResponse = await fetch(`${SQUARE_BASE_URL}/bookings/${appointmentId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        booking: {
          id: appointmentId,
          version: appointmentData.booking.version,
          customer_note: newNote
        }
      })
    });

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json();
      console.error('Check-in submission error:', errorData);
      throw new Error('Failed to update appointment');
    }

    // Send notification to Jason (mock implementation)
    console.log('Sending notification to Jason about check-in');
    // In a real implementation, you would use a notification service like Twilio or SendGrid

    return {
      success: true,
      photoUrl
    };

  } catch (error) {
    console.error('Error submitting check-in:', error);
    return {
      success: false,
      error: 'Unable to complete check-in at this time'
    };
  }
}

// Helper function to retrieve specific booking by ID (using bulk approach)
export async function retrieveBooking(bookingId) {
  try {
    const response = await fetch(`${SQUARE_BASE_URL}/bookings/bulk-retrieve`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        booking_ids: [bookingId]
      })
    });

    if (!response.ok) {
      throw new Error('Failed to retrieve booking');
    }

    const data = await response.json();
    return data.bookings?.[0] || null;
  } catch (error) {
    console.error('Error retrieving booking:', error);
    return null;
  }
}

// Helper function to list all bookings (for debugging)
export async function listAllBookings() {
  try {
    const response = await fetch(`${SQUARE_BASE_URL}/bookings?limit=100`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to list bookings');
    }

    const data = await response.json();
    return data.bookings || [];
  } catch (error) {
    console.error('Error listing bookings:', error);
    return [];
  }
}

// Helper function to create a test customer (for development only - remove in production)
export async function createTestCustomer() {
  try {
    const response = await fetch(`${SQUARE_BASE_URL}/customers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ACCESS_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        given_name: 'Test',
        family_name: 'Customer',
        email_address: 'test@example.com',
        phone_number: '+15551234567'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create test customer');
    }

    const data = await response.json();
    return data.customer;
  } catch (error) {
    console.error('Error creating test customer:', error);
    return null;
  }
}